import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Download,
  ExternalLink,
  Image as ImageIcon
} from "lucide-react";

/**
 * Utility to determine if an attachment URL is an image
 */
export const isImageAttachment = (url) => {
  if (!url || typeof url !== "string") return false;
  const cleanUrl = url.split("?")[0].split("#")[0].toLowerCase();
  const imageExtensions = [
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".webp",
    ".svg",
    ".bmp",
    ".heic",
    ".heif",
    ".avif",
    ".ico",
    ".tiff"
  ];
  if (imageExtensions.some(ext => cleanUrl.endsWith(ext))) return true;
  if (url.startsWith("data:image/") || url.startsWith("blob:")) return true;
  if (/\/(images|photos|media|avatars|uploads|attachments|pictures)\//i.test(url)) return true;
  return false;
};

/**
 * FullScreenImageViewer component
 * Renders full-screen lightbox modal via React Portal with zoom, rotate, download, and multi-image navigation.
 */
export default function FullScreenImageViewer({
  isOpen = false,
  images = [],
  initialIndex = 0,
  title = "Attachment Preview",
  onClose,
}) {
  // Normalize images to array of strings
  const imageList = Array.isArray(images)
    ? images.map(img => (typeof img === "string" ? img : img?.url || img?.src || ""))
    : typeof images === "string" && images
    ? [images]
    : [];

  const [currentIndex, setCurrentIndex] = useState(initialIndex || 0);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  // Sync currentIndex with initialIndex when modal opens or initialIndex changes
  useEffect(() => {
    if (isOpen) {
      const validIndex = Math.max(0, Math.min(initialIndex, imageList.length - 1));
      setCurrentIndex(validIndex);
      setZoom(1);
      setRotation(0);
      setLoading(true);
      setHasError(false);
      setDragOffset({ x: 0, y: 0 });
    }
  }, [isOpen, initialIndex, imageList.length]);

  // Lock body scroll when viewer is open
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const currentImage = imageList[currentIndex] || "";

  // Reset zoom & rotation when changing image
  const handleSelectImage = useCallback((index) => {
    if (index >= 0 && index < imageList.length) {
      setCurrentIndex(index);
      setZoom(1);
      setRotation(0);
      setLoading(true);
      setHasError(false);
      setDragOffset({ x: 0, y: 0 });
    }
  }, [imageList.length]);

  const handleNext = useCallback(() => {
    if (imageList.length > 1) {
      handleSelectImage((currentIndex + 1) % imageList.length);
    }
  }, [currentIndex, imageList.length, handleSelectImage]);

  const handlePrev = useCallback(() => {
    if (imageList.length > 1) {
      handleSelectImage((currentIndex - 1 + imageList.length) % imageList.length);
    }
  }, [currentIndex, imageList.length, handleSelectImage]);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.5, 3.5));
  };

  const handleZoomOut = () => {
    setZoom(prev => {
      const next = Math.max(prev - 0.5, 1);
      if (next === 1) setDragOffset({ x: 0, y: 0 });
      return next;
    });
  };

  const handleResetZoom = () => {
    setZoom(1);
    setRotation(0);
    setDragOffset({ x: 0, y: 0 });
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleDownload = async () => {
    if (!currentImage) return;
    const filename = `image-${currentIndex + 1}-${Date.now()}.png`;
    try {
      const response = await fetch(currentImage, { mode: "cors" });
      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
        return;
      }
    } catch {
      // Fallback for CORS-restricted URLs
    }
    const link = document.createElement("a");
    link.href = currentImage;
    link.download = filename;
    link.target = "_blank";
    link.rel = "noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        handleNext();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        handlePrev();
      } else if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        handleZoomIn();
      } else if (e.key === "-") {
        e.preventDefault();
        handleZoomOut();
      } else if (e.key === "0") {
        e.preventDefault();
        handleResetZoom();
      } else if (e.key === "r" || e.key === "R") {
        e.preventDefault();
        handleRotate();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleNext, handlePrev, onClose]);

  // Pan / drag when zoomed in
  const handleMouseDown = (e) => {
    if (zoom > 1) {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({ x: e.clientX - dragOffset.x, y: e.clientY - dragOffset.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      setDragOffset({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  if (!isOpen || imageList.length === 0) return null;

  return createPortal(
    <div
      className="fullscreen-image-viewer"
      ref={containerRef}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100000,
        backgroundColor: "rgba(10, 15, 29, 0.94)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "stretch",
        userSelect: "none",
        animation: "fsViewerFadeIn 0.22s ease-out forwards",
      }}
      onClick={(e) => {
        if (e.target === containerRef.current || e.target.classList.contains("fs-viewer-backdrop-click")) {
          onClose?.();
        }
      }}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {/* ── TOP TOOLBAR ────────────────────────────────────────── */}
      <div
        className="d-flex align-items-center justify-content-between px-6 py-4"
        style={{
          background: "linear-gradient(180deg, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0) 100%)",
          zIndex: 10,
        }}
      >
        {/* Title & Counter */}
        <div className="d-flex align-items-center gap-3 text-white">
          <div
            className="d-flex align-items-center justify-content-center rounded-circle"
            style={{ width: 38, height: 38, background: "rgba(255, 255, 255, 0.12)" }}
          >
            <ImageIcon className="w-5 h-5 text-success" />
          </div>
          <div>
            <div className="fs-6 fw-bold text-white text-truncate" style={{ maxWidth: "45vw" }}>
              {title}
            </div>
            {imageList.length > 1 && (
              <div className="fs-8 text-white-50">
                Image {currentIndex + 1} of {imageList.length}
              </div>
            )}
          </div>
        </div>

        {/* Action Controls */}
        <div className="d-flex align-items-center gap-2">
          {/* Zoom Out */}
          <button
            type="button"
            className="btn btn-icon btn-sm rounded-circle text-white border-0"
            style={{ background: "rgba(255, 255, 255, 0.12)", width: 38, height: 38 }}
            onClick={handleZoomOut}
            disabled={zoom <= 1}
            title="Zoom out (-)"
          >
            <ZoomOut className="w-4 h-4" />
          </button>

          {/* Zoom Percentage / Reset */}
          <button
            type="button"
            className="btn btn-sm text-white px-3 py-1 rounded-pill border-0 fw-semibold fs-8"
            style={{ background: "rgba(255, 255, 255, 0.12)", minWidth: 60 }}
            onClick={handleResetZoom}
            title="Reset Zoom (0)"
          >
            {Math.round(zoom * 100)}%
          </button>

          {/* Zoom In */}
          <button
            type="button"
            className="btn btn-icon btn-sm rounded-circle text-white border-0"
            style={{ background: "rgba(255, 255, 255, 0.12)", width: 38, height: 38 }}
            onClick={handleZoomIn}
            disabled={zoom >= 3.5}
            title="Zoom in (+)"
          >
            <ZoomIn className="w-4 h-4" />
          </button>

          {/* Rotate */}
          <button
            type="button"
            className="btn btn-icon btn-sm rounded-circle text-white border-0"
            style={{ background: "rgba(255, 255, 255, 0.12)", width: 38, height: 38 }}
            onClick={handleRotate}
            title="Rotate (R)"
          >
            <RotateCw className="w-4 h-4" />
          </button>

          {/* Download */}
          <button
            type="button"
            className="btn btn-icon btn-sm rounded-circle text-white border-0"
            style={{ background: "rgba(255, 255, 255, 0.12)", width: 38, height: 38 }}
            onClick={handleDownload}
            title="Download Image"
          >
            <Download className="w-4 h-4" />
          </button>

          {/* Open in New Tab */}
          <a
            href={currentImage}
            target="_blank"
            rel="noreferrer"
            className="btn btn-icon btn-sm rounded-circle text-white border-0 d-inline-flex align-items-center justify-content-center"
            style={{ background: "rgba(255, 255, 255, 0.12)", width: 38, height: 38 }}
            title="Open original in new tab"
          >
            <ExternalLink className="w-4 h-4" />
          </a>

          <div className="vr mx-2 bg-white bg-opacity-25" style={{ height: 24 }}></div>

          {/* Close Button */}
          <button
            type="button"
            className="btn btn-icon btn-sm rounded-circle text-white border-0"
            style={{ background: "#ef4444", width: 38, height: 38 }}
            onClick={onClose}
            title="Close (Esc)"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* ── MAIN IMAGE VIEWPORT ─────────────────────────────────── */}
      <div
        className="fs-viewer-backdrop-click flex-grow-1 position-relative d-flex align-items-center justify-content-center overflow-hidden p-4"
        style={{ cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default" }}
        onMouseDown={handleMouseDown}
      >
        {/* Left Arrow (Previous) */}
        {imageList.length > 1 && (
          <button
            type="button"
            className="btn btn-icon rounded-circle position-absolute start-0 ms-6 text-white border-0 shadow-lg d-flex align-items-center justify-content-center"
            style={{
              zIndex: 20,
              width: 48,
              height: 48,
              background: "rgba(255, 255, 255, 0.15)",
              backdropFilter: "blur(6px)",
              transition: "transform 0.2s ease, background 0.2s ease",
            }}
            onClick={(e) => {
              e.stopPropagation();
              handlePrev();
            }}
            title="Previous (Left Arrow)"
          >
            <ChevronLeft className="w-7 h-7" />
          </button>
        )}

        {/* Loading Spinner */}
        {loading && !hasError && (
          <div className="position-absolute d-flex flex-column align-items-center text-white gap-3" style={{ zIndex: 5 }}>
            <div className="spinner-border text-success" style={{ width: "3rem", height: "3rem" }} role="status"></div>
            <span className="fs-7 text-white-50 fw-semibold">Loading full resolution image...</span>
          </div>
        )}

        {/* Image Display */}
        {!hasError ? (
          <img
            key={currentImage}
            src={currentImage}
            alt={title || `Image ${currentIndex + 1}`}
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setHasError(true);
            }}
            style={{
              maxWidth: zoom === 1 ? "90vw" : "none",
              maxHeight: zoom === 1 ? "75vh" : "none",
              objectFit: "contain",
              borderRadius: "10px",
              boxShadow: "0 25px 60px -15px rgba(0, 0, 0, 0.7)",
              transform: `translate(${dragOffset.x}px, ${dragOffset.y}px) scale(${zoom}) rotate(${rotation}deg)`,
              transition: isDragging ? "none" : "transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.2s ease",
              opacity: loading ? 0 : 1,
              pointerEvents: "auto",
            }}
            draggable={false}
          />
        ) : (
          <div className="text-center p-8 rounded-4 bg-dark bg-opacity-50 border border-white border-opacity-10 text-white" style={{ maxWidth: 400 }}>
            <div className="d-flex align-items-center justify-content-center mb-3">
              <ImageIcon className="w-12 h-12 text-danger" />
            </div>
            <h5 className="text-white fw-bold mb-2">Unable to Load Preview</h5>
            <p className="text-white-50 fs-7 mb-4">
              The image file could not be displayed directly. You can try opening it in a new tab.
            </p>
            <a
              href={currentImage}
              target="_blank"
              rel="noreferrer"
              className="btn btn-success btn-sm px-5 fw-bold"
            >
              <ExternalLink className="w-4 h-4 me-2" /> Open External Link
            </a>
          </div>
        )}

        {/* Right Arrow (Next) */}
        {imageList.length > 1 && (
          <button
            type="button"
            className="btn btn-icon rounded-circle position-absolute end-0 me-6 text-white border-0 shadow-lg d-flex align-items-center justify-content-center"
            style={{
              zIndex: 20,
              width: 48,
              height: 48,
              background: "rgba(255, 255, 255, 0.15)",
              backdropFilter: "blur(6px)",
              transition: "transform 0.2s ease, background 0.2s ease",
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            title="Next (Right Arrow)"
          >
            <ChevronRight className="w-7 h-7" />
          </button>
        )}
      </div>

      {/* ── BOTTOM THUMBNAILS STRIP (If multiple images) ────────── */}
      {imageList.length > 1 && (
        <div
          className="d-flex align-items-center justify-content-center gap-3 px-6 py-4 overflow-x-auto"
          style={{
            background: "linear-gradient(0deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 100%)",
            zIndex: 10,
          }}
        >
          {imageList.map((url, idx) => {
            const isActive = idx === currentIndex;
            return (
              <div
                key={idx}
                role="button"
                tabIndex={0}
                onClick={() => handleSelectImage(idx)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") handleSelectImage(idx);
                }}
                className={`position-relative rounded-3 overflow-hidden cursor-pointer flex-shrink-0 ${
                  isActive ? "border border-2 border-success shadow" : "border border-transparent opacity-50"
                }`}
                style={{
                  width: 60,
                  height: 60,
                  transition: "all 0.2s ease",
                  transform: isActive ? "scale(1.08)" : "scale(1)",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                }}
                title={`View image ${idx + 1}`}
              >
                <img
                  src={url}
                  alt={`Thumbnail ${idx + 1}`}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* CSS Keyframes for smooth entrance */}
      <style>{`
        @keyframes fsViewerFadeIn {
          0% { opacity: 0; transform: scale(0.98); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>,
    document.body
  );
}
